import { useState } from 'react'
import {
    Alert, Badge, Button, Form, InputGroup, Modal, Spinner, Table
} from 'react-bootstrap'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_AUTOMATIONS } from '../../gql/queries/campaigns'
import { DELETE_AUTOMATION, TOGGLE_AUTOMATION } from '../../gql/mutations/campaigns'
import { Automation } from '../../types/campaigns'
import { AutomationWizard } from './components/AutomationWizard'
import { AutomationLogModal } from './components/AutomationLogModal'
import { CampaignsNav } from '../campaigns/components/CampaignsNav'

const TRIGGER_LABELS: Record<string, string> = {
    HAPPY_BIRTHDAY: '🎂 Happy Birthday',
    CONDITIONAL_LEAD: '🎯 Conditional (Leads)',
    CONDITIONAL_POLICY: '🛡️ Conditional (Policy)',
}

export const AutomationsPage = () => {
    const [search, setSearch] = useState('')
    const [channelFilter, setChannelFilter] = useState('')
    const [wizardOpen, setWizardOpen] = useState(false)
    const [editingAuto, setEditingAuto] = useState<Automation | null>(null)
    const [logAutoId, setLogAutoId] = useState<Automation | null>(null)
    const [deletingAuto, setDeletingAuto] = useState<Automation | null>(null)

    const { data, loading, error, refetch } = useQuery<{ automations: Automation[] }>(
        GET_AUTOMATIONS,
        {
            variables: { channel: channelFilter || undefined },
            fetchPolicy: 'cache-and-network',
        }
    )

    const [toggleAutomation] = useMutation(TOGGLE_AUTOMATION)
    const [deleteAutomation, { loading: deleting }] = useMutation(DELETE_AUTOMATION)

    const automations = (data?.automations ?? []).filter(
        (a) =>
            !search ||
            a.name.toLowerCase().includes(search.toLowerCase()) ||
            a.description?.toLowerCase().includes(search.toLowerCase())
    )

    const handleToggle = async (automation: Automation) => {
        try {
            await toggleAutomation({ variables: { id: automation.id } })
            toast.success(`Automation ${automation.isActive ? 'disabled' : 'enabled'}`)
            await refetch()
        } catch {
            toast.error('Error changing status')
        }
    }

    const handleConfirmDelete = async () => {
        if (!deletingAuto) return
        try {
            const { data } = await deleteAutomation({ variables: { id: deletingAuto.id } })
            if (data?.deleteAutomation?.ok) {
                toast.success('Automation deleted')
                setDeletingAuto(null)
                await refetch()
            } else {
                toast.error(data?.deleteAutomation?.errors?.[0] || 'Error deleting automation')
            }
        } catch {
            toast.error('Error deleting automation')
        }
    }

    const handleNew = () => {
        setEditingAuto(null)
        setWizardOpen(true)
    }

    const handleEdit = (a: Automation) => {
        setEditingAuto(a)
        setWizardOpen(true)
    }

    return (
        <div className='automations-page'>
            <CampaignsNav />
            {/* Header */}
            <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
                <div>
                    <h1 className='page-title'>⚡ Automations</h1>
                    <div className='text-muted'>
                        Automated Email and SMS messages based on policy and payment events.
                    </div>
                </div>
                <Button variant='primary' onClick={handleNew}>
                    <i className='bi bi-plus-lg me-1'></i>New Automation
                </Button>
            </div>

            {/* Filters */}
            <div className='d-flex gap-3 mb-5 flex-wrap'>
                <InputGroup style={{ maxWidth: 320 }}>
                    <InputGroup.Text><i className='bi bi-search'></i></InputGroup.Text>
                    <Form.Control
                        placeholder='Search automation...'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </InputGroup>
                <Form.Select
                    style={{ maxWidth: 180 }}
                    value={channelFilter}
                    onChange={(e) => setChannelFilter(e.target.value)}
                >
                    <option value=''>All channels</option>
                    <option value='EMAIL'>Email</option>
                    <option value='SMS'>SMS</option>
                </Form.Select>
            </div>

            {/* Error */}
            {error && <Alert variant='danger'>Error loading automations: {error.message}</Alert>}

            {/* Loading */}
            {loading && !data && (
                <div className='text-center py-5'>
                    <Spinner animation='border' />
                </div>
            )}

            {/* Empty */}
            {!loading && automations.length === 0 && (
                <div className='text-center py-5 text-muted'>
                    <i className='bi bi-lightning-charge' style={{ fontSize: 64 }}></i>
                    <h5 className='mt-3'>No automations yet</h5>
                    <p>Create your first automation to start sending messages automatically.</p>
                    <Button variant='primary' onClick={handleNew}>
                        <i className='bi bi-plus-lg me-1'></i>Create first automation
                    </Button>
                </div>
            )}

            {/* Table */}
            {automations.length > 0 && (
                <div className='card'>
                    <div className='card-body p-0'>
                        <Table hover responsive className='mb-0 align-middle'>
                            <thead className='table-light'>
                                <tr>
                                    <th style={{ width: 40 }}></th>
                                    <th>Name</th>
                                    <th>Scope</th>
                                    <th>Trigger</th>
                                    <th>Channel</th>
                                    <th>Template</th>
                                    <th>Sent</th>
                                    <th>Last sent</th>
                                    <th style={{ width: 120 }} className='text-end pe-4'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {automations.map((a) => (
                                    <tr key={a.id}>
                                        <td className='ps-4'>
                                            <Form.Check
                                                type='switch'
                                                id={`auto-switch-${a.id}`}
                                                checked={a.isActive}
                                                onChange={() => handleToggle(a)}
                                                title={a.isActive ? 'Active — click to disable' : 'Inactive — click to enable'}
                                            />
                                        </td>
                                        <td>
                                            <div className='fw-semibold'>{a.name}</div>
                                            {a.description && (
                                                <div className='text-muted small' style={{ maxWidth: 280 }}>
                                                    {a.description}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <Badge
                                                bg={a.scope === 'ORGANIZATION' ? 'secondary' : 'light'}
                                                text={a.scope === 'ORGANIZATION' ? 'white' : 'dark'}
                                                className='border'
                                            >
                                                {a.scope === 'ORGANIZATION' ? 'Org' : 'Personal'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className='small fw-semibold'>{TRIGGER_LABELS[a.triggerType] ?? a.triggerType}</div>
                                            {(a.triggerType === 'CONDITIONAL_LEAD' || a.triggerType === 'CONDITIONAL_POLICY') && a.triggerConfig?.condition_rule && (
                                                <div className='text-muted' style={{ fontSize: '0.75rem', marginTop: 2 }}>
                                                    {a.triggerConfig.condition_rule}
                                                    {a.triggerConfig.duration_days ? ` (${a.triggerConfig.duration_days}d)` : ''}
                                                    {a.triggerConfig.lead_stage ? ` [${a.triggerConfig.lead_stage}]` : ''}
                                                    {a.triggerConfig.carrier ? ` [${a.triggerConfig.carrier}]` : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <Badge
                                                bg={a.channel === 'EMAIL' ? 'primary' : 'info'}
                                                className='text-white'
                                            >
                                                {a.channel === 'EMAIL' ? 'Email' : 'SMS'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {a.template
                                                ? <span className='small'>{a.template.name}</span>
                                                : <span className='text-danger small'>No template</span>
                                            }
                                        </td>
                                        <td className='text-center'>
                                            <button
                                                className='btn btn-link btn-sm p-0 text-decoration-none'
                                                onClick={() => setLogAutoId(a)}
                                                title='View history'
                                            >
                                                {a.sentCount ?? 0}{' '}
                                                <span className='text-muted'>/ {a.logCount ?? 0}</span>
                                            </button>
                                        </td>
                                        <td>
                                            {a.lastRunAt
                                                ? <span className='small text-muted'>{new Date(a.lastRunAt).toLocaleDateString()}</span>
                                                : <span className='text-muted small'>—</span>
                                            }
                                        </td>
                                        <td className='text-end pe-4'>
                                            <div className='d-flex gap-1 justify-content-end'>
                                                <Button
                                                    variant='light'
                                                    size='sm'
                                                    className='btn-icon'
                                                    onClick={() => handleEdit(a)}
                                                    title='Edit'
                                                >
                                                    <i className='bi bi-pencil fs-6'></i>
                                                </Button>
                                                <Button
                                                    variant='light'
                                                    size='sm'
                                                    className='btn-icon'
                                                    onClick={() => setLogAutoId(a)}
                                                    title='View history'
                                                >
                                                    <i className='bi bi-clock-history fs-6'></i>
                                                </Button>
                                                <Button
                                                    variant='light-danger'
                                                    size='sm'
                                                    className='btn-icon'
                                                    onClick={() => setDeletingAuto(a)}
                                                    title='Delete'
                                                >
                                                    <i className='bi bi-trash fs-6'></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Wizard */}
            <AutomationWizard
                show={wizardOpen}
                onHide={() => setWizardOpen(false)}
                onSaved={() => refetch()}
                automation={editingAuto}
            />

            {/* Log History Modal */}
            <AutomationLogModal
                show={!!logAutoId}
                onHide={() => setLogAutoId(null)}
                automation={logAutoId}
            />

            {/* Delete Confirmation Modal */}
            <Modal show={!!deletingAuto} onHide={() => setDeletingAuto(null)} centered>
                <Modal.Header closeButton className='border-0 pb-0'>
                    <Modal.Title className='text-danger'>Delete Automation</Modal.Title>
                </Modal.Header>
                <Modal.Body className='pt-4 pb-4 text-center'>
                    <div className='btn btn-icon btn-light-danger btn-circle btn-lg mb-3'>
                        <i className='bi bi-trash fs-1'></i>
                    </div>
                    <h5>Are you sure?</h5>
                    <div className='text-muted'>
                        You are about to delete <span className='fw-bold'>"{deletingAuto?.name}"</span>.
                        This cannot be undone.
                    </div>
                </Modal.Body>
                <Modal.Footer className='border-0 justify-content-center pb-4'>
                    <Button variant='light' onClick={() => setDeletingAuto(null)}>Cancel</Button>
                    <Button variant='danger' disabled={deleting} onClick={handleConfirmDelete}>
                        {deleting && <Spinner animation='border' size='sm' className='me-1' />}
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
